import React, { ReactNode, useMemo } from "react";
import { cx, isoToLocalDate, useTranslations } from "@jugl-web/utils";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { Button, MultiSectionLayout } from "@jugl-web/ui-kit";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { getFileSizeLabel } from "@jugl-web/utils/utils/files";
import { useKnowledgeTraining } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { CanceledSubscriptionSection } from "./components/CanceledSubscriptionSection";
import { TrainingStatusPill } from "../TrainingStatusPill";
import { ReactComponent as WebsiteIcon } from "./assets/website.svg";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import { ReactComponent as QuestionIcon } from "./assets/question.svg";
import { ReactComponent as TextIcon } from "./assets/text.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { AgentKnowledgeMenuItem } from "./components/AgentKnowledgeMenuItem";
import { AgentTrainingSource } from "../../types";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";

export const AgentKnowledgeMenu: React.FC<{
  selectedSource: AgentTrainingSource;
  entityId: string;
  onSourceSelect: (source: AgentTrainingSource) => void;
}> = ({ selectedSource, entityId, onSourceSelect }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { isAnonymous } = useAnonymousSession();
  const { defaultAgentKnowledge, isSubscriptionCanceled } = useSubscriptionV2();
  const { humanizePastDate } = useFormattedDate();
  const { isTotalSizeExceeded } = useKnowledgeTraining();
  const CXSignupDialog = useCXSignupDialog();

  const {
    docxKnowledge,
    linksKnowledge,
    pdfKnowledge,
    quesKnowledge,
    textKnowledge,
    knowledgeInfo,
    isAnyKnowledgeError,
    isAnyKnowledgeLoading,
    hasAnyKnowledge,
    untrainedKnowledgeIds,
  } = useAgentKnowledge(entityId);

  const { trainingStatus, trainAgent, isTrainingLoading } =
    useKnowledgeTraining();
  const isFirstTraining = !knowledgeInfo?.info.last_updated;
  const highlightedText = (content: ReactNode) => (
    <span
      className="cursor-pointer font-medium text-primary-900 underline"
      onClick={() => {
        if (isAnonymous) {
          CXSignupDialog?.open();
          return;
        }
        navigateToPage("cxWorkspacePlans");
      }}
    >
      {content}
    </span>
  );

  const { files, questions, text, website } = useMemo(() => {
    if (isAnyKnowledgeLoading || isAnyKnowledgeError) {
      return {
        website: { count: 0, size: 0 },
        text: { count: 0, size: 0 },
        files: { count: 0, size: 0 },
        questions: { count: 0, size: 0 },
      };
    }

    const getStats = (items: Array<KnowledgeItem[] | undefined>) => {
      let count = 0;
      let size = 0;

      items.forEach((arr) => {
        if (!arr) return;
        arr.forEach((item) => {
          if (!item) return;
          count += 1;
          size += item.content_size ?? 0;
        });
      });

      return {
        count,
        size,
      };
    };

    return {
      website: getStats([linksKnowledge]),
      text: getStats([textKnowledge]),
      files: getStats([pdfKnowledge, docxKnowledge]),
      questions: getStats([quesKnowledge]),
    };
  }, [
    linksKnowledge,
    textKnowledge,
    pdfKnowledge,
    docxKnowledge,
    quesKnowledge,
    isAnyKnowledgeLoading,
    isAnyKnowledgeError,
  ]);

  const menuItems: {
    type: AgentTrainingSource;
    icon: React.ReactNode;
    title: string;
    count: number;
    size: number;
    isDisabled: boolean;
  }[] = useMemo(
    () => [
      {
        type: AgentTrainingSource.website,
        icon: <WebsiteIcon />,
        title: t({
          id: "cx-ai-agents-page.website",
          defaultMessage: "Website",
        }),
        count: website.count,
        size: website.size,
        isDisabled: false,
      },
      {
        type: AgentTrainingSource.files,
        icon: <FileIcon />,
        title: t({
          id: "cx-ai-agents-page.files",
          defaultMessage: "Files",
        }),
        count: files.count,
        size: files.size,
        isDisabled: isAnonymous,
      },
      {
        type: AgentTrainingSource.text,
        icon: <TextIcon />,
        title: t({
          id: "cx-ai-agents-page.text",
          defaultMessage: "Text",
        }),
        count: text.count,
        size: text.size,
        isDisabled: isAnonymous,
      },
      {
        type: AgentTrainingSource.questions,
        icon: <QuestionIcon />,
        title: t({
          id: "cx-ai-agents-page.questions-and-answers",
          defaultMessage: "Q&A",
        }),
        count: questions.count,
        size: questions.size,
        isDisabled: isAnonymous,
      },
    ],
    [
      t,
      website.count,
      website.size,
      files.count,
      files.size,
      text.count,
      text.size,
      isAnonymous,
      questions.count,
      questions.size,
    ]
  );

  return (
    <MultiSectionLayout
      footer={
        !isTotalSizeExceeded &&
        !!untrainedKnowledgeIds?.length &&
        trainingStatus !== "in_process" && (
          <div className="border-0 border-t border-r border-solid border-dark-100 p-6 pt-4">
            <Button
              size="lg"
              className="w-full shrink-0"
              iconEnd={<ArrowIcon />}
              isDisabled={isTrainingLoading}
              onClick={() => trainAgent(untrainedKnowledgeIds)}
            >
              {isFirstTraining
                ? t({
                    id: "cx-ai-agents-page.train-agent",
                    defaultMessage: "Train Agent",
                  })
                : t({
                    id: "cx-ai-agents-page.retrain-agent",
                    defaultMessage: "Retrain Agent",
                  })}
            </Button>
          </div>
        )
      }
    >
      <div className="jugl__custom-scrollbar flex h-full w-[480px] flex-col gap-10 overflow-y-auto border-0 border-r border-solid border-dark-100 p-8">
        <div className="flex flex-col gap-1.5">
          <span className="font-secondary text-lg leading-[27px] font-medium text-black">
            {t({
              id: "cx-ai-agents-page.training-sources",
              defaultMessage: "📚 Training Sources",
            })}
          </span>
          <div className="flex items-center">
            {hasAnyKnowledge && (
              <TrainingStatusPill
                isFirstTraining={isFirstTraining}
                status={
                  trainingStatus === "in_process"
                    ? "in_process"
                    : !untrainedKnowledgeIds
                      ? "processing"
                      : untrainedKnowledgeIds.length
                        ? "need_retrain"
                        : "completed"
                }
              />
            )}
            {hasAnyKnowledge &&
              trainingStatus === "completed" &&
              knowledgeInfo?.info.last_updated && (
                <div className="flex items-center font-secondary text-xs text-dark-600">
                  <div className="mx-1 flex w-[18px] items-center justify-center">
                    •
                  </div>
                  <span>
                    {humanizePastDate(
                      isoToLocalDate(knowledgeInfo.info.last_updated)
                    )}
                  </span>
                </div>
              )}
          </div>
        </div>
        <CanceledSubscriptionSection />
        <div className="flex flex-col gap-6">
          {menuItems.map((item) => (
            <AgentKnowledgeMenuItem
              key={item.type}
              {...item}
              isSelected={item.type === selectedSource}
              onClick={() => {
                if (item.isDisabled) {
                  CXSignupDialog?.open();
                  return;
                }
                onSourceSelect(item.type);
              }}
            />
          ))}
          {knowledgeInfo &&
            !isSubscriptionCanceled &&
            defaultAgentKnowledge > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-secondary leading-4 text-dark">
                    {t({
                      id: "cx-ai-agents-page.total-size",
                      defaultMessage: "Total size",
                    })}
                  </span>
                  <div>
                    <span className="font-secondary text-sm leading-[21px] text-dark-700">
                      {getFileSizeLabel(knowledgeInfo.info.total_size || 0)} /
                    </span>{" "}
                    <span className="font-secondary text-sm leading-[21px] text-dark">
                      {defaultAgentKnowledge} MB
                    </span>
                  </div>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-[14px] bg-dark-100">
                  <div
                    className={cx(
                      "h-full w-1/4 rounded-[19px] bg-linear-to-r from-[#2196F3] to-[#CC41D3]",
                      isTotalSizeExceeded && "from-[#5B2B2C] to-[#FF4144]"
                    )}
                    style={{
                      width: `${Math.min(
                        ((knowledgeInfo.info.total_size || 0) /
                          1024 /
                          1024 /
                          defaultAgentKnowledge) *
                          100,
                        100
                      )}%`,
                    }}
                  />
                </div>
                {isTotalSizeExceeded && (
                  <div className="mt-2 flex items-center justify-center gap-3 rounded-xl bg-tertiary-50 p-3">
                    <InfoIcon className="shrink-0" />
                    <span className="font-secondary text-sm text-dark">
                      {isFirstTraining
                        ? t(
                            {
                              id: "cx-ai-agents-page.sources-size-limit-exceeded-upgrade-plan-training",
                              defaultMessage:
                                "Sources size limit is reached. Remove some sources or <highlightedText>upgrade your plan</highlightedText> to continue Agent training",
                            },
                            { highlightedText }
                          )
                        : t(
                            {
                              id: "cx-ai-agents-page.sources-size-limit-exceeded-upgrade-plan",
                              defaultMessage:
                                "Sources size limit is reached. Remove some sources or <highlightedText>upgrade your plan</highlightedText> to continue Agent retraining",
                            },
                            { highlightedText }
                          )}
                    </span>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </MultiSectionLayout>
  );
};
